"use client";

import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useTranslation } from "@/lib/i18n";
import { CONNECTION_RETRY_CLEAR_EVENT, CONNECTION_RETRY_EVENT } from "@/lib/http";

type RetryNoticeDetail = {
  id: string;
  retryAfterMs: number;
  retry: () => void;
};

type ClearNoticeDetail = {
  id: string;
};

export function ConnectionRetryToaster() {
  const t = useTranslation();
  const intervalsRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const clearTicker = (id: string) => {
      const intervalId = intervalsRef.current.get(id);
      if (intervalId) {
        window.clearInterval(intervalId);
        intervalsRef.current.delete(id);
      }
    };

    const renderNotice = (detail: RetryNoticeDetail, remainingMs: number) => {
      const seconds = Math.max(1, Math.ceil(remainingMs / 1000));
      toast.error(t("error.connection"), {
        id: detail.id,
        duration: Infinity,
        description: t("error.connection_retry_in").replace("{seconds}", String(seconds)),
        action: {
          label: t("common.retry"),
          onClick: () => {
            clearTicker(detail.id);
            toast.dismiss(detail.id);
            detail.retry();
          },
        },
      });
    };

    const onRetryNotice = (event: Event) => {
      const detail = (event as CustomEvent<RetryNoticeDetail>).detail;
      clearTicker(detail.id);

      let remainingMs = detail.retryAfterMs;
      renderNotice(detail, remainingMs);

      const intervalId = window.setInterval(() => {
        remainingMs -= 1000;
        if (remainingMs <= 0) {
          clearTicker(detail.id);
          return;
        }
        renderNotice(detail, remainingMs);
      }, 1000);

      intervalsRef.current.set(detail.id, intervalId);
    };

    const onClearNotice = (event: Event) => {
      const detail = (event as CustomEvent<ClearNoticeDetail>).detail;
      clearTicker(detail.id);
      toast.dismiss(detail.id);
    };

    window.addEventListener(CONNECTION_RETRY_EVENT, onRetryNotice as EventListener);
    window.addEventListener(CONNECTION_RETRY_CLEAR_EVENT, onClearNotice as EventListener);

    return () => {
      window.removeEventListener(CONNECTION_RETRY_EVENT, onRetryNotice as EventListener);
      window.removeEventListener(CONNECTION_RETRY_CLEAR_EVENT, onClearNotice as EventListener);
      intervalsRef.current.forEach((intervalId) => window.clearInterval(intervalId));
      intervalsRef.current.clear();
    };
  }, [t]);

  return null;
}
